import React, { useState, useRef } from 'react';
import TEMPLATES, { JH_TEMPLATES, V_TEMPLATES } from '../templates';

export default function BulkGenerator() {
  const [selectedTemplates, setSelectedTemplates] = useState(['1', '7']);
  const [headlines, setHeadlines] = useState('FOLLOW THIS\nAsbestos\nHow was this allowed?');
  const [personImage, setPersonImage] = useState('');
  const [subjectImage, setSubjectImage] = useState('');
  const [bgColor, setBgColor] = useState('#111111');
  const [accentColor, setAccentColor] = useState('#E53935');
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [results, setResults] = useState([]);
  const abortRef = useRef(null);

  const toggleTemplate = (id) => {
    setSelectedTemplates(prev =>
      prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]
    );
  };

  const handleUpload = async (file, setter) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('image', file);
    try {
      const res = await fetch('/api/upload', { method: 'POST', body: formData });
      const json = await res.json();
      if (json.filename) setter(json.filename);
    } catch (err) {
      console.error('Upload failed:', err);
    }
  };

  const handleGenerate = async () => {
    const headlineList = headlines.split('\n').map(h => h.trim()).filter(Boolean);
    if (!headlineList.length || !selectedTemplates.length) return;

    setGenerating(true);
    setResults([]);

    // Build items array: template × headline combinations
    const items = [];
    for (const tid of selectedTemplates) {
      for (const headline of headlineList) {
        items.push({
          templateId: tid,
          data: {
            headline,
            personImage,
            subjectImage,
            bgColor,
            accentColor,
          },
        });
      }
    }

    setProgress({ current: 0, total: items.length });

    try {
      const res = await fetch('/api/generate-bulk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.trim()) continue;
          try {
            const result = JSON.parse(line);
            setResults(prev => [...prev, result]);
            setProgress(prev => ({ ...prev, current: result.index + 1 }));
          } catch {}
        }
      }
    } catch (err) {
      console.error('Bulk generate failed:', err);
    }

    setGenerating(false);
  };

  const successResults = results.filter(r => r.success);

  return (
    <div className="bulk-page">
      <h2>Bulk Generate</h2>

      {/* Template Selection */}
      <div className="bulk-section">
        <h3>Select Templates</h3>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--jh-color)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>
          Jeremy Haynes
        </div>
        <div className="template-check-grid">
          {JH_TEMPLATES.map(t => (
            <label key={t.id} className="template-check">
              <input type="checkbox" checked={selectedTemplates.includes(t.id)}
                onChange={() => toggleTemplate(t.id)} />
              {t.id}. {t.name}
            </label>
          ))}
        </div>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--v-color)', marginTop: 12, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>
          Veritasium
        </div>
        <div className="template-check-grid">
          {V_TEMPLATES.map(t => (
            <label key={t.id} className="template-check">
              <input type="checkbox" checked={selectedTemplates.includes(t.id)}
                onChange={() => toggleTemplate(t.id)} />
              {t.id}. {t.name}
            </label>
          ))}
        </div>
      </div>

      {/* Headlines */}
      <div className="bulk-section">
        <h3>Headlines (one per line)</h3>
        <textarea className="control-input control-textarea" rows={6}
          value={headlines} onChange={e => setHeadlines(e.target.value)}
          placeholder="Enter headlines, one per line..." />
        <div style={{ marginTop: 6, fontSize: 12, color: 'var(--text-muted)' }}>
          {headlines.split('\n').filter(h => h.trim()).length} headlines × {selectedTemplates.length} templates = {headlines.split('\n').filter(h => h.trim()).length * selectedTemplates.length} thumbnails
        </div>
      </div>

      {/* Images + Colors */}
      <div className="bulk-section">
        <h3>Images & Colors</h3>
        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <label className="control-label">Person Image (JH templates)</label>
            <input type="file" accept="image/*" style={{ marginTop: 6 }}
              onChange={e => handleUpload(e.target.files[0], setPersonImage)} />
            {personImage && <div style={{ fontSize: 12, color: 'var(--jh-color)', marginTop: 4 }}>{personImage}</div>}
          </div>
          <div style={{ flex: 1 }}>
            <label className="control-label">Subject Image (V templates)</label>
            <input type="file" accept="image/*" style={{ marginTop: 6 }}
              onChange={e => handleUpload(e.target.files[0], setSubjectImage)} />
            {subjectImage && <div style={{ fontSize: 12, color: 'var(--v-color)', marginTop: 4 }}>{subjectImage}</div>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
          <div className="color-row">
            <input type="color" className="color-swatch" value={bgColor}
              onChange={e => setBgColor(e.target.value)} />
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Background</span>
          </div>
          <div className="color-row">
            <input type="color" className="color-swatch" value={accentColor}
              onChange={e => setAccentColor(e.target.value)} />
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Accent</span>
          </div>
        </div>
      </div>

      {/* Generate Button */}
      <button className="generate-btn" onClick={handleGenerate}
        disabled={generating || !selectedTemplates.length}>
        {generating
          ? `Generating... ${progress.current}/${progress.total}`
          : `Generate ${headlines.split('\n').filter(h => h.trim()).length * selectedTemplates.length} Thumbnails`}
      </button>

      {/* Progress */}
      {generating && (
        <div className="progress-bar-wrap">
          <div className="progress-bar-fill"
            style={{ width: `${progress.total ? (progress.current / progress.total * 100) : 0}%` }} />
        </div>
      )}

      {/* Results */}
      {successResults.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 12 }}>
            Generated ({successResults.length})
          </h3>
          <div className="result-grid">
            {successResults.map((r, i) => (
              <div key={i} className="result-card">
                <img src={r.url} alt={r.headline} />
                <div className="result-card-info">
                  <span>T{r.templateId}: {r.headline?.slice(0, 30)}</span>
                  <a href={r.url} target="_blank" rel="noreferrer">Download</a>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
